import React, { useEffect, useMemo, useState } from "react";
import Header from "../core/Header";
import Footer from "../core/Footer";

const API_BASE = "https://disease.sh/v3/covid-19";

const formatNumber = (value) => new Intl.NumberFormat("en-US").format(value || 0);
const formatDateTime = (value) =>
  value ? new Date(value).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "N/A";

const Covid = () => {
  const [globalData, setGlobalData] = useState(null);
  const [countries, setCountries] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchCovidData = async () => {
      setLoading(true);
      setError("");

      try {
        const [globalResponse, countriesResponse] = await Promise.all([
          fetch(`${API_BASE}/all`),
          fetch(`${API_BASE}/countries?sort=cases`),
        ]);

        if (!globalResponse.ok || !countriesResponse.ok) {
          throw new Error("Unable to fetch COVID-19 data.");
        }

        const [globalJson, countriesJson] = await Promise.all([
          globalResponse.json(),
          countriesResponse.json(),
        ]);

        setGlobalData(globalJson);
        setCountries(Array.isArray(countriesJson) ? countriesJson : []);
      } catch (fetchError) {
        setError(fetchError.message || "Unable to fetch COVID-19 data.");
      } finally {
        setLoading(false);
      }
    };

    fetchCovidData();
  }, []);

  const filteredCountries = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return countries.slice(0, 20);

    return countries
      .filter((country) => country.country.toLowerCase().includes(query))
      .slice(0, 20);
  }, [countries, searchQuery]);

  return (
    <div>
      <Header />

      <div className="container py-4">
        <h4 className="text-center pb-3">COVID-19 Dashboard</h4>
        <p className="text-center text-muted">Source: disease.sh API</p>

        {loading && <h5 className="text-center">Loading...</h5>}
        {!loading && error && <h5 className="text-center text-danger">{error}</h5>}

        {!loading && !error && globalData && (
          <div className="row pb-4">
            <div className="col-md-4 pb-3">
              <div className="card p-3">
                <h6>Total Cases</h6>
                <strong>{formatNumber(globalData.cases)}</strong>
              </div>
            </div>
            <div className="col-md-4 pb-3">
              <div className="card p-3">
                <h6>Total Deaths</h6>
                <strong>{formatNumber(globalData.deaths)}</strong>
              </div>
            </div>
            <div className="col-md-4 pb-3">
              <div className="card p-3">
                <h6>Total Recovered</h6>
                <strong>{formatNumber(globalData.recovered)}</strong>
              </div>
            </div>
            <div className="col-12">
              <p className="text-muted">Last Updated: {formatDateTime(globalData.updated)}</p>
            </div>
          </div>
        )}

        {!loading && !error && (
          <div>
            <div className="form-group">
              <input
                type="text"
                className="form-control"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Filter countries..."
              />
            </div>

            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>Country</th>
                    <th>Cases</th>
                    <th>Deaths</th>
                    <th>Recovered</th>
                    <th>Active</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCountries.map((country) => (
                    <tr key={country.country}>
                      <td>{country.country}</td>
                      <td>{formatNumber(country.cases)}</td>
                      <td>{formatNumber(country.deaths)}</td>
                      <td>{formatNumber(country.recovered)}</td>
                      <td>{formatNumber(country.active)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Covid;
